import { useEffect, useState } from "react";
import { Download, Eye, RefreshCw } from "lucide-react";
import {
  useAndesAttempt,
  useAndesAttemptShot,
  useAndesAttempts,
  useDownloadAndesAttemptPackage,
} from "@/hooks/queries";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";
import type { AndesEmitAttempt, AndesEmitOutcome, AndesEmitRequestPayload } from "@/types";

const OUTCOME_TONE: Record<AndesEmitOutcome, "success" | "info" | "danger"> = {
  submitted: "success",
  dry_run: "info",
  error: "danger",
};

const OUTCOME_LABEL: Record<AndesEmitOutcome, string> = {
  submitted: "enviado",
  dry_run: "dry-run",
  error: "error",
};

function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function FieldTable({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-[minmax(8rem,12rem)_1fr] gap-x-3 gap-y-1 rounded border border-slate-200 bg-white p-2 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="font-medium text-slate-500">{k}</dt>
          <dd className="break-all font-mono text-slate-800">
            {typeof v === "string" || typeof v === "number" || typeof v === "boolean"
              ? String(v)
              : JSON.stringify(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function AttemptShot({
  subscriberId,
  attemptId,
  name,
}: {
  subscriberId: string;
  attemptId: string;
  name: string;
}) {
  const shot = useAndesAttemptShot(subscriberId, attemptId, name);
  useEffect(() => {
    const url = shot.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [shot.data]);

  if (shot.isPending) {
    return <div className="h-32 animate-pulse rounded border border-slate-200 bg-slate-50" />;
  }
  if (shot.isError || !shot.data) {
    return <p className="text-xs text-red-700">No se pudo cargar {name}</p>;
  }
  return (
    <figure className="space-y-1">
      <figcaption className="text-xs text-slate-500">{name}</figcaption>
      <img src={shot.data} alt={name} className="w-full rounded border border-slate-200" />
    </figure>
  );
}

function RequestPayloadView({ payload }: { payload: AndesEmitRequestPayload }) {
  return (
    <div className="space-y-3">
      <div className="font-medium">Información enviada</div>
      <FieldTable
        data={{
          personType: payload.personType,
          documentNumber: payload.documentNumber,
          subscriberName: payload.subscriberName,
          certTypeLabel: payload.certTypeLabel,
          dryRun: payload.dryRun,
        }}
      />
      {payload.gridRow && (
        <div>
          <div className="mb-1 text-xs font-medium text-slate-600">Fila de la grilla</div>
          <FieldTable data={payload.gridRow} />
        </div>
      )}
      {payload.entidad && (
        <div>
          <div className="mb-1 text-xs font-medium text-slate-600">Datos de la Entidad</div>
          <FieldTable data={payload.entidad} />
        </div>
      )}
      {payload.vigencia && (
        <div>
          <div className="mb-1 text-xs font-medium text-slate-600">Vigencia</div>
          <FieldTable data={payload.vigencia} />
        </div>
      )}
      {payload.csrPem && (
        <div>
          <div className="mb-1 text-xs font-medium text-slate-600">CSR pegado en Andes</div>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-2 font-mono text-[11px] text-slate-700">
            {payload.csrPem}
          </pre>
        </div>
      )}
    </div>
  );
}

function AttemptDetail({
  subscriberId,
  attemptId,
}: {
  subscriberId: string;
  attemptId: string;
}) {
  const detail = useAndesAttempt(subscriberId, attemptId);
  const downloadPkg = useDownloadAndesAttemptPackage(subscriberId);
  if (detail.isPending) return <CenteredSpinner label="Cargando intento…" />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => detail.refetch()} />;
  const a = detail.data;
  const hasPackage = Boolean(a.packageFileName || a.packageByteSize);
  return (
    <div className="space-y-4 text-sm">
      <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-1">
        <div>
          <span className="font-medium">Resultado:</span>{" "}
          <Badge tone={OUTCOME_TONE[a.outcome]}>{OUTCOME_LABEL[a.outcome]}</Badge>
          {a.dryRun ? " · dry-run" : " · live"}
        </div>
        {a.errorCode && (
          <div>
            <span className="font-medium">Código:</span> {a.errorCode}
          </div>
        )}
        {(a.errorMessage || a.bridgerMessage) && (
          <div className="text-slate-600">{a.errorMessage || a.bridgerMessage}</div>
        )}
      </div>

      {a.requestPayload && <RequestPayloadView payload={a.requestPayload} />}

      <div>
        <div className="font-medium mb-1">Paquete enviado</div>
        {a.soporteFiles.length > 0 && (
          <div className="mb-2 text-xs text-slate-600">
            Archivos: {a.soporteFiles.join(", ")}
          </div>
        )}
        {hasPackage ? (
          <Button
            size="sm"
            variant="outline"
            disabled={downloadPkg.isPending}
            onClick={() => downloadPkg.mutate(attemptId)}
          >
            <Download className="h-3.5 w-3.5" />
            {a.packageFileName ?? "soporte.zip"}
            {a.packageByteSize != null ? ` (${formatBytes(a.packageByteSize)})` : ""}
          </Button>
        ) : (
          <p className="text-xs text-slate-500">
            Este intento no llegó a armar el ZIP de soporte (falló antes).
          </p>
        )}
        {downloadPkg.isError && (
          <p className="mt-1 text-xs text-red-700">No se pudo descargar el paquete.</p>
        )}
      </div>

      {(a.confirmTitle || a.confirmBody) && (
        <div>
          <div className="font-medium mb-1">Diálogo Andes</div>
          {a.confirmTitle && <div className="text-slate-800">{a.confirmTitle}</div>}
          {a.confirmBody && (
            <pre className="mt-1 whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
              {a.confirmBody}
            </pre>
          )}
        </div>
      )}

      <div>
        <div className="font-medium mb-1">Log del envío</div>
        {(a.events ?? []).length > 0 ? (
          <ol className="max-h-64 space-y-1 overflow-auto rounded border border-slate-200 bg-slate-50 p-2 font-mono text-[11px] text-slate-700">
            {(a.events ?? []).map((ev, i) => (
              <li key={`${ev.ts}-${i}`}>
                <span className="text-slate-400">{ev.ts.slice(11, 19)}</span> {ev.level} {ev.msg}
                {ev.data ? ` ${JSON.stringify(ev.data)}` : ""}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-xs text-slate-500">
            Sin eventos del panel (el intento falló antes de conducir Andes, o el bridger no
            devolvió el log).
          </p>
        )}
      </div>

      {(a.shots ?? []).length > 0 && (
        <div className="space-y-2">
          <div className="font-medium">Capturas</div>
          {a.shots!.map((s) => (
            <AttemptShot key={s.name} subscriberId={subscriberId} attemptId={attemptId} name={s.name} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AndesTab({ subscriberId }: { subscriberId: string }) {
  const list = useAndesAttempts(subscriberId);
  const [openId, setOpenId] = useState<string | null>(null);
  const attempts = list.data?.data ?? [];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>Andes</CardTitle>
          <p className="mt-0.5 text-xs text-slate-500">
            Cada dry-run y envío queda guardado aquí (éxito y error), con la información
            estructurada, el ZIP de soporte, capturas y el log del panel.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => list.refetch()} aria-label="Refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </CardHeader>

      {list.isPending ? (
        <CenteredSpinner />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : attempts.length === 0 ? (
        <EmptyState
          title="Sin intentos"
          description="Aún no hay emisiones a Andes. Un dry-run o un envío desde Solicitudes aparece aquí."
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Fecha</TH>
              <TH>Modo</TH>
              <TH>Resultado</TH>
              <TH>Código</TH>
              <TH>Paquete</TH>
              <TH>Mensaje</TH>
              <TH className="text-right">Acciones</TH>
            </tr>
          </THead>
          <TBody>
            {attempts.map((a: AndesEmitAttempt) => (
              <TR key={a.id}>
                <TD className="whitespace-nowrap text-xs">{formatDate(a.createdAt)}</TD>
                <TD>{a.dryRun ? "dry-run" : "envío"}</TD>
                <TD>
                  <Badge tone={OUTCOME_TONE[a.outcome]}>{OUTCOME_LABEL[a.outcome]}</Badge>
                </TD>
                <TD className="font-mono text-xs">{a.errorCode ?? "—"}</TD>
                <TD className="text-xs text-slate-600">
                  {a.packageFileName
                    ? `${a.packageFileName} (${formatBytes(a.packageByteSize)})`
                    : a.soporteFiles.length > 0
                      ? a.soporteFiles.join(", ")
                      : "—"}
                </TD>
                <TD className="max-w-xs truncate text-xs text-slate-600">
                  {a.errorMessage || a.bridgerMessage || "—"}
                </TD>
                <TD className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setOpenId(a.id)}>
                    <Eye className="h-3.5 w-3.5" /> Ver
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <Dialog
        open={!!openId}
        onClose={() => setOpenId(null)}
        title="Intento Andes"
        description={openId ?? undefined}
        className="max-w-3xl"
      >
        {openId && <AttemptDetail subscriberId={subscriberId} attemptId={openId} />}
      </Dialog>
    </Card>
  );
}
